import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { AuthCard } from "../components/AuthCard";
import { api, formatApiError } from "../lib/api";

const NEUTRAL = "If that email has an account, we've sent a reset link.";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError("Please enter your email");
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard eyebrow="Password help" title="Forgot your password?" testid="forgot-password-card">
      {sent ? (
        <div data-testid="forgot-password-sent" className="mt-8 space-y-6">
          <div className="flex gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-4 text-sm font-semibold text-blue-800">
            <MailCheck className="h-5 w-5 shrink-0" />
            <p data-testid="forgot-password-message">{NEUTRAL}</p>
          </div>
          <p className="text-sm text-slate-500">The link expires in 1 hour. Check your spam folder if it doesn't arrive.</p>
          <Link to="/login" data-testid="forgot-back-to-login" className="block text-center text-sm font-bold text-blue-700 hover:text-blue-800">Back to log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="mt-8 space-y-5" data-testid="forgot-password-form">
          <p className="text-sm text-slate-600">Enter the email you use to log in and we'll send you a link to choose a new password.</p>
          <div className="space-y-1.5">
            <Label htmlFor="forgot-email" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</Label>
            <Input id="forgot-email" type="email" autoComplete="email" data-testid="forgot-email-input" className="h-12 rounded-xl bg-white" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          {error && <p data-testid="forgot-error" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="forgot-submit-button" className="h-12 w-full rounded-lg text-base font-semibold">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
          </Button>
          <Link to="/login" data-testid="forgot-back-link" className="block text-center text-sm font-bold text-blue-700 hover:text-blue-800">Back to log in</Link>
        </form>
      )}
    </AuthCard>
  );
}
